import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { crypto as cv, type dto } from '@photo-vault/shared';
import { Button, Card, Icon, PageEyebrow } from '../../ui/index.js';
import { useAuthSession } from '../../auth/AuthContext.js';
import { useInfra } from '../../infra/context.js';
import { getAlbumKey } from '../albums/albumKeys.js';
import {
  scanRootForDuplicates,
  type DuplicateCluster,
  type ScanProgress,
  type ScanResult,
} from './scan.js';
import { DuplicateClusterCard } from './DuplicateClusterCard.js';

interface LoadedRoot {
  rootId: string;
  rootName: string;
  albumKey: CryptoKey;
  // albumId → ['root', 'sub', 'leaf'] for breadcrumbs in cluster cards.
  albumPaths: Map<string, string[]>;
}

type LoadState = 'loading' | LoadedRoot | { error: string };

interface ScanState {
  status: 'idle' | 'running' | 'done' | { error: string };
  progress?: ScanProgress;
  result?: ScanResult;
}

function formatBytes(n: number): string {
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function DuplicatesPage() {
  const { rootId } = useParams<{ rootId: string }>();
  const session = useAuthSession();
  const { metadata } = useInfra();
  const [load, setLoad] = useState<LoadState>('loading');
  const [scan, setScan] = useState<ScanState>({ status: 'idle' });
  const [presigned, setPresigned] = useState<Map<string, string>>(new Map());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [verifyingHuge, setVerifyingHuge] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // Load root identity (name, albumKey, album path lookup) once.
  useEffect(() => {
    if (!rootId) return;
    setLoad('loading');
    setScan({ status: 'idle' });
    setPresigned(new Map());
    setSelected(new Set());

    void (async () => {
      try {
        const { albums } = await metadata.listAlbums(session.jwt);
        const root = albums.find((a) => a.id === rootId);
        if (!root || !root.wrappedAlbumKey || root.parentId !== null) {
          setLoad({ error: 'album radice non trovato' });
          return;
        }
        const albumKey = await getAlbumKey(rootId, root.wrappedAlbumKey, session.rsaPriv);

        const { nodes } = await metadata.fetchAlbumTree(rootId, session.jwt);
        const decryptedNames = new Map<string, string>();
        await Promise.all(
          nodes.map(async (n) => {
            try {
              const name = await cv.decryptText(n.encryptedName, albumKey);
              decryptedNames.set(n.id, name);
            } catch {
              decryptedNames.set(n.id, '(decrittura fallita)');
            }
          }),
        );
        const parentOf = new Map<string, string | null>();
        for (const n of nodes) parentOf.set(n.id, n.parentId);

        const albumPaths = new Map<string, string[]>();
        const pathFor = (id: string): string[] => {
          const cached = albumPaths.get(id);
          if (cached) return cached;
          const out: string[] = [];
          const seen = new Set<string>();
          let cursor: string | null | undefined = id;
          while (cursor && !seen.has(cursor)) {
            seen.add(cursor);
            out.unshift(decryptedNames.get(cursor) ?? cursor);
            cursor = parentOf.get(cursor) ?? null;
          }
          albumPaths.set(id, out);
          return out;
        };
        for (const n of nodes) pathFor(n.id);

        setLoad({
          rootId,
          rootName: decryptedNames.get(rootId) ?? rootId,
          albumKey,
          albumPaths,
        });
      } catch (err) {
        setLoad({ error: err instanceof Error ? err.message : 'errore' });
      }
    })();
  }, [rootId, session.jwt, metadata]);

  // Abort the in-flight scan when the page unmounts (or the root changes).
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, [rootId]);

  async function startScan(verifyHuge: boolean) {
    if (!rootId || typeof load !== 'object' || 'error' in load) return;
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    setScan({
      status: 'running',
      progress: { phase: 'fetching', total: 0, hashed: 0, unverified: 0 },
    });
    if (verifyHuge) setVerifyingHuge(true);

    try {
      const result = await scanRootForDuplicates({
        rootId,
        albumKey: load.albumKey,
        token: session.jwt,
        metadata,
        signal: ac.signal,
        verifyHugeClusters: verifyHuge,
        onProgress: (p) => setScan((s) => ({ ...s, status: 'running', progress: p })),
      });

      setScan({ status: 'done', result });
      // Pre-select the duplicates-to-delete: every copy except the oldest.
      const initial = new Set<string>();
      for (const c of result.clusters) {
        if (c.kind !== 'verified') continue;
        const sorted = [...c.photos].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
        for (let i = 1; i < sorted.length; i++) initial.add(sorted[i]!.photoId);
      }
      setSelected(initial);

      // Stream presigned URLs for every photo we'll render.
      const items: dto.DownloadBatchRequest['items'] = [];
      for (const c of result.clusters) {
        for (const p of c.photos) items.push({ albumId: p.albumId, photoId: p.photoId });
      }
      if (items.length > 0) {
        await metadata.downloadsBatchAllStreaming(items, session.jwt, (chunk) => {
          setPresigned((curr) => {
            const next = new Map(curr);
            for (const it of chunk) next.set(it.photoId, it.url);
            return next;
          });
        });
      }
    } catch (err) {
      if (ac.signal.aborted) return;
      setScan({ status: { error: err instanceof Error ? err.message : 'errore' } });
    } finally {
      if (verifyHuge) setVerifyingHuge(false);
    }
  }

  function toggle(photoId: string) {
    setSelected((curr) => {
      const next = new Set(curr);
      if (next.has(photoId)) next.delete(photoId);
      else next.add(photoId);
      return next;
    });
  }

  async function handleBulkDelete() {
    if (typeof load !== 'object' || 'error' in load) return;
    if (selected.size === 0) return;
    if (scan.status !== 'done' || !scan.result) return;
    if (
      !window.confirm(
        `Eliminare ${selected.size} ${selected.size === 1 ? 'foto' : 'foto'}? L'operazione è irreversibile.`,
      )
    )
      return;

    setDeleting(true);
    try {
      // Build (albumId, photoId) pairs from the result so we don't need to
      // look them up again. Chunk at 200 (the API cap).
      const items: dto.DeletePhotosRequest['items'] = [];
      for (const c of scan.result.clusters) {
        for (const p of c.photos) {
          if (selected.has(p.photoId)) {
            items.push({ albumId: p.albumId, photoId: p.photoId });
          }
        }
      }
      const CHUNK = 200;
      for (let i = 0; i < items.length; i += CHUNK) {
        await metadata.deletePhotosBatch({ items: items.slice(i, i + CHUNK) }, session.jwt);
      }

      // Update local state: drop deleted photos, prune clusters with <2 left.
      const removed = new Set(items.map((it) => it.photoId));
      const newClusters: DuplicateCluster[] = [];
      for (const c of scan.result.clusters) {
        const remaining = c.photos.filter((p) => !removed.has(p.photoId));
        if (remaining.length < 2) continue;
        newClusters.push({ ...c, photos: remaining });
      }
      setScan({
        status: 'done',
        result: { ...scan.result, clusters: newClusters },
      });
      setSelected(new Set());
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'errore');
    } finally {
      setDeleting(false);
    }
  }

  if (!rootId) return null;
  if (load === 'loading') return <p className="py-6 text-sm text-on-surface-variant">Carico…</p>;
  if ('error' in load) return <p className="py-6 text-sm text-error">Errore: {load.error}</p>;

  const verified = scan.result?.clusters.filter((c) => c.kind === 'verified') ?? [];
  const unverified = scan.result?.clusters.filter((c) => c.kind === 'unverified') ?? [];

  return (
    <div className="flex flex-col gap-7 pb-24">
      <div className="flex flex-col gap-3">
        <PageEyebrow>Duplicati</PageEyebrow>
        <div className="flex flex-wrap items-baseline gap-2">
          <h2 className="font-display text-2xl font-semibold">{load.rootName}</h2>
          <Link
            to={`/albums/${rootId}`}
            className="text-sm text-on-surface-variant transition-colors hover:text-cyan"
          >
            ← torna all'album
          </Link>
        </div>
      </div>

      {scan.status === 'idle' ? (
        <Card className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <Icon name="search" className="text-2xl text-cyan" />
            <h3 className="font-display text-lg font-semibold">Scansiona duplicati</h3>
          </div>
          <p className="text-sm text-on-surface-variant">
            La scansione confronta dimensione e contenuto (SHA-256) di tutte le foto sotto questo
            album radice. Foto piccole vengono scaricate e cifrate in parallelo; i file molto grandi
            (oltre 100 MB) sono trattati uno alla volta. I cluster con molti GB totali sono mostrati
            come «non verificati» finché non confermi tu.
          </p>
          <Button onClick={() => void startScan(false)} leftIcon={<Icon name="search" />}>
            Avvia scansione
          </Button>
        </Card>
      ) : null}

      {scan.status === 'running' || scan.status === 'done' ? (
        <ScanStatusBar
          progress={scan.progress}
          done={scan.status === 'done'}
          result={scan.result}
        />
      ) : null}

      {typeof scan.status === 'object' && 'error' in scan.status ? (
        <Card className="border-error/40">
          <p className="text-sm text-error">Scansione fallita: {scan.status.error}</p>
        </Card>
      ) : null}

      {scan.status === 'done' && verified.length === 0 && unverified.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 py-10 text-center">
          <Icon name="check_circle" className="text-3xl text-cyan" />
          <p className="text-sm text-on-surface-variant">
            Nessun duplicato trovato in {scan.result?.totalPhotos ?? 0} foto.
          </p>
        </Card>
      ) : null}

      {verified.length > 0 ? (
        <div className="flex flex-col gap-4">
          <h3 className="font-display text-lg font-semibold">
            {verified.length} {verified.length === 1 ? 'gruppo verificato' : 'gruppi verificati'}
          </h3>
          {verified.map((c, idx) => (
            <DuplicateClusterCard
              key={`v-${c.hash}-${idx}`}
              cluster={c}
              albumKey={load.albumKey}
              presigned={presigned}
              albumPathFor={(id) => load.albumPaths.get(id) ?? []}
              selectedIds={selected}
              onToggle={toggle}
            />
          ))}
        </div>
      ) : null}

      {unverified.length > 0 ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <h3 className="font-display text-lg font-semibold">
              {unverified.length}{' '}
              {unverified.length === 1 ? 'gruppo non verificato' : 'gruppi non verificati'}
            </h3>
            {scan.result && scan.result.pendingVerificationBytes > 0 ? (
              <Button
                variant="outline"
                onClick={() => void startScan(true)}
                loading={verifyingHuge}
                leftIcon={<Icon name="verified" />}
              >
                Verifica anche i grandi ({formatBytes(scan.result.pendingVerificationBytes)} di
                download)
              </Button>
            ) : null}
          </div>
          {unverified.map((c, idx) => (
            <DuplicateClusterCard
              key={`u-${c.size}-${idx}`}
              cluster={c}
              albumKey={load.albumKey}
              presigned={presigned}
              albumPathFor={(id) => load.albumPaths.get(id) ?? []}
              selectedIds={selected}
              onToggle={toggle}
            />
          ))}
        </div>
      ) : null}

      {selected.size > 0 ? (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-outline-variant bg-surface/95 px-4 py-3 backdrop-blur">
          <div className="mx-auto flex max-w-5xl items-center justify-between gap-3">
            <span className="text-sm text-on-surface-variant">
              {selected.size} {selected.size === 1 ? 'foto selezionata' : 'foto selezionate'}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => setSelected(new Set())}>
                Annulla
              </Button>
              <Button
                variant="danger"
                loading={deleting}
                onClick={() => void handleBulkDelete()}
                leftIcon={<Icon name="delete_forever" />}
              >
                Elimina
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ScanStatusBar({
  progress,
  done,
  result,
}: {
  progress: ScanProgress | undefined;
  done: boolean;
  result: ScanResult | undefined;
}) {
  const phaseLabel = !progress
    ? '…'
    : progress.phase === 'fetching'
      ? 'Carico elenco foto…'
      : progress.phase === 'tier1'
        ? 'Pre-cluster per dimensione…'
        : progress.phase === 'tier2'
          ? `Calcolo hash ${progress.hashed}/${progress.total}…`
          : 'Completato';

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <Icon name={done ? 'check_circle' : 'autorenew'} className="text-2xl text-cyan" />
        <div className="flex flex-col">
          <span className="font-display text-base font-semibold">{phaseLabel}</span>
          {result ? (
            <span className="text-xs text-on-surface-variant">
              {result.totalPhotos} foto totali · {result.uniqueBySize} uniche per dimensione ·{' '}
              {result.hashed} hashate
              {result.pendingVerificationBytes > 0
                ? ` · ${formatBytes(result.pendingVerificationBytes)} non verificati`
                : ''}
            </span>
          ) : null}
        </div>
      </div>
      {progress && progress.phase === 'tier2' && progress.total > 0 ? (
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-container">
          <div
            className="h-full bg-cyan transition-all"
            style={{ width: `${Math.round((progress.hashed / progress.total) * 100)}%` }}
          />
        </div>
      ) : null}
    </Card>
  );
}
